import { AgGridReact } from "ag-grid-react";
import React, { useCallback, useMemo, useRef, useEffect, forwardRef, useImperativeHandle, useState } from "react";
import { instance } from "../../utilites/ApiClient";
import "./TableStyle.scss";
import PriorityRenderer from "./PriorityRenderer";
import {
  actionValueRenderer,
  checkboxRenderer,
  iconRenderer,
  hyperlinkRenderer,
} from "./tableFunctions";

import 'ag-grid-enterprise';
import { Button } from "react-bootstrap";
import { EXPORT } from "../../constants/Constants";
import { FaFileExport, FaFileArrowDown, FaFilterCircleXmark } from "react-icons/fa6";

// ── Custom Header Checkbox (v27 SSRM compatible) ───────────────────────────
const CheckboxHeader = (props) => {
  const { api } = props;
  const [checked, setChecked] = useState(false);
  const [indeterminate, setIndeterminate] = useState(false);
  const inputRef = useRef(null);

  const updateState = useCallback(() => {
    const total = api.getDisplayedRowCount();
    const selected = api.getSelectedRows().length;
    if (selected === 0) {
      setChecked(false);
      setIndeterminate(false);
    } else if (selected >= total && total > 0) {
      setChecked(true);
      setIndeterminate(false);
    } else {
      setChecked(false);
      setIndeterminate(true);
    }
  }, [api]);

  useEffect(() => {
    if (!api) return;
    api.addEventListener('selectionChanged', updateState);
    api.addEventListener('modelUpdated', updateState);
    return () => {
      try {
        api.removeEventListener('selectionChanged', updateState);
        api.removeEventListener('modelUpdated', updateState);
      } catch (e) { /* grid already destroyed */ }
    };
  }, [api, updateState]);

  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  const handleChange = (e) => {
    if (e.target.checked) {
      api.forEachNode((node) => { if (node.data) node.setSelected(true); });
    } else {
      api.deselectAll();
    }
  };

  return (
    <div style={{ whiteSpace: 'normal', wordBreak: 'normal', overflowWrap: 'break-word', lineHeight: '1.5', paddingTop: '15px', paddingBottom: '10px' }}>
      <input
        ref={inputRef}
        type="checkbox"
        checked={checked}
        onChange={handleChange}
        style={{ cursor: 'pointer', width: '16px', height: '16px', margin: 0 }}
      />
    </div>
  );
};

// ── CSV helpers ────────────────────────────────────────────────────────────
const EXCLUDED_EXPORT_COLS = ['checkbox', 'actions'];

function getExportColumns(colApi) {
    return (colApi?.getAllDisplayedColumns?.() ?? [])
      .filter((c) => !EXCLUDED_EXPORT_COLS.includes(c.getColId()))
      .map((c) => ({ field: c.getColDef().field, header: c.getColDef().headerName || c.getColDef().field }));
  }

  function rowsToCsv(rows, cols) {
    const esc = (v) => {
      if (v === null || v === undefined) return '';
      const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const header = cols.map((c) => esc(c.header)).join(',');
    const body = rows.map((r) => cols.map((c) => esc(r?.[c.field])).join(',')).join('\n');
    return `${header}\n${body}`;
  }

  function downloadCsv(csv, fileName) {
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  }


// ── Module-level constants ─────────────────────────────────────────────────
const priorityComparator = (valueA, valueB) => {
  const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
  const a = priorityOrder[valueA?.toLowerCase()] || 0;
  const b = priorityOrder[valueB?.toLowerCase()] || 0;
  return a - b;
};

const WRAP_CELL_STYLE = {
  whiteSpace: 'pre-wrap',
  wordWrap: 'break-word',
  lineHeight: 'normal',
  paddingBottom: '7px',
  textAlign: 'left',
};

// ── Helper to build state in saved column order ────────────────────────────
function buildOrderedState(currentState, columnOrder, visibility) {
  const newState = columnOrder.map((savedCol) => {
    const currentCol = currentState.find((c) => c.colId === savedCol.colId) || {};
    return {
      colId: savedCol.colId,
      width: savedCol.width || currentCol.width,
      pinned: savedCol.pinned || currentCol.pinned || null,
      hide: visibility.hasOwnProperty(savedCol.colId)
        ? !visibility[savedCol.colId]
        : (currentCol.hide || false),
    };
  });
  // Append any columns not in saved order
  currentState.forEach((col) => {
    if (!newState.find((c) => c.colId === col.colId)) {
      newState.push({ colId: col.colId, width: col.width, pinned: col.pinned || null });
    }
  });
  return newState;
}

// ── Shared: apply external filter/sort/column state ────────────────────────
function applyExternalFilter(api, colApi, filter, clientSide) {
  if (!filter) return;

  if (filter.filterModel) {
    api.setFilterModel(filter.filterModel);
  }

  if (colApi?.applyColumnState) {
    const currentState = colApi.getColumnState?.() ?? [];
    const visibility = filter.columnVisibility ?? {};
    const columnOrder = filter.columnOrder ?? [];
    const sortMap = Object.fromEntries(
      (filter.sortModel ?? []).map((s, i) => [s.colId, { sort: s.sort, sortIndex: i }])
    );

    const newState = columnOrder.length > 0
      ? buildOrderedState(currentState, columnOrder, visibility).map((col) => {
          const sortInfo = sortMap[col.colId] ?? { sort: null, sortIndex: null };
          return { ...col, sort: sortInfo.sort, sortIndex: sortInfo.sortIndex };
        })
      : currentState.map((col) => {
          const sortInfo = sortMap[col.colId] ?? { sort: null, sortIndex: null };
          return {
            colId: col.colId,
            width: col.width,
            pinned: col.pinned,
            hide: visibility.hasOwnProperty(col.colId) ? !visibility[col.colId] : col.hide,
            sort: sortInfo.sort,
            sortIndex: sortInfo.sortIndex,
          };
        });

    colApi.applyColumnState({
      state: newState,
      applyOrder: columnOrder.length > 0,
      defaultState: { sort: null },
    });
  }

  // Server-side needs a cache purge after filter apply
  if (!clientSide) {
    setTimeout(() => {
      if (api.refreshServerSide)          api.refreshServerSide({ purge: true });
      else if (api.refreshServerSideStore) api.refreshServerSideStore({ purge: true });
      else if (api.purgeServerSideCache)   api.purgeServerSideCache();
    }, 100);
  }
}
// ──────────────────────────────────────────────────────────────────────────

const ServerSideGrid = forwardRef(({
  // ── Mode ──────────────────────────────────────────────
  clientSide = false,

  // ── Client-side props ─────────────────────────────────
  rowData = [],
  loading = false,

  // ── Server-side props ─────────────────────────────────
  apiEndpoint,
  apiPayload = {},
  cacheBlockSize = 80,

  // ── Shared props ──────────────────────────────────────
  columnDefs,
  onRowClicked,
  tableHeight = "calc(100vh - 230px)",   // was "70vh"
  rowHeight = 48,
  showPagination = true,
  paginationPageSize = 80,
  defaultColDef,
  externalFilter = null,
  theme = "alpine",
  onFilterChanged,
  onSortChanged,
  onSelectionChanged,
  onColumnStateChanged,
  onSaveFilter,
  filterType,
  onGridStabilized,
  clickAdd,
  clickEdit,
  clickDelete,
  clickView,
  clickDownload,
  clickStatus,
  showExport = false,
  showExportAll = false,
  showClearFilter = false,
  exportFileName = "export",
  exportAllPageSize = 100000,
}, ref) => {
  const gridRef = useRef(null);
  const [columnApi, setColumnApi] = useState(null);
  const columnApiRef = useRef(null);
  const externalFilterRef = useRef(externalFilter);
  const onGridStabilizedRef = useRef(onGridStabilized);
  const initialFilterRef = useRef(null);
  const hasAppliedInitialOrder = useRef(false);

  useEffect(() => { externalFilterRef.current = externalFilter; });
  useEffect(() => { onGridStabilizedRef.current = onGridStabilized; }, [onGridStabilized]);

  // ── Ref API ───────────────────────────────────────────────────────────────
  useImperativeHandle(ref, () => ({
    exportDataAsCsv: (params) => gridRef.current?.api?.exportDataAsCsv(params),
    getSelectedRows: () => gridRef.current?.api?.getSelectedRows() || [],
    getApi: () => gridRef.current?.api,
    getColumnApi: () => columnApiRef.current,
    saveFilter: (meta) => handleSaveFilter(meta),
    getCurrentFilterCriteria: () => buildCurrentFilterCriteria(gridRef.current?.api, columnApiRef.current),
    exportSelected: () => handleExport(),
    exportAll: () => handleExportAll(),
    clearFilter: () => handleClearFilter(),
  }), [columnApi]);

  // ── Default column def ────────────────────────────────────────────────────
  const defaultColumnDef = useMemo(() => ({
    resizable: true,
    sortable: true,
    filter: true,
    floatingFilter: false,
    suppressHeaderMenuButton: false,
    menuTabs: ['filterMenuTab'],
    wrapText: true,
    autoHeight: true,
    ...(clientSide && {
    filterParams: {
      defaultToNothingSelected: true,
    },
  }),
    cellStyle: {
      whiteSpace: 'normal',
      wordBreak: 'normal',
      overflowWrap: 'break-word',
      lineHeight: '1.5',
      paddingTop: '10px',
      paddingBottom: '10px',
    },
    ...defaultColDef,
  }), [defaultColDef]);

  // ── Process special column types ──────────────────────────────────────────
  const processedColumnDefs = useMemo(() => {
    return columnDefs.map((col) => {

      if (col.field === "actions") {
        return {
          headerName: col.headerName || "Actions",
          field: col.field,
          cellRenderer: actionValueRenderer,
          cellRendererParams: { clickAdd, clickEdit, clickDelete, clickView, clickDownload, clickStatus },
          hide: col.hide ?? false,
          suppressMenu: true,
          suppressFiltersToolPanel: true,
          suppressColumnsToolPanel: true,
          width: col.width,
          pinned: col.pinned,
          wrapText: true,
          autoHeight: true,
        };
      }

      if (col.type === "checkbox") {
        return {
          headerName: col.headerName || col.display_name,
          field: col.field,
          hide: col.hide ?? false,
          floatingFilter: false,
          suppressMenu: true,
          cellRenderer: checkboxRenderer,
          pinned: col.pinned,
          cellClass: "cell-left-align",
          wrapText: true,
          autoHeight: true,
        };
      }

      if (col.type === "bool" || col.type === "icon") {
        return {
          headerName: col.headerName || col.display_name,
          field: col.field,
          hide: col.hide ?? false,
          floatingFilter: false,
          suppressMenu: true,
          width: col.width,
          icon: col.icon ?? "",
          cellRenderer: iconRenderer,
          pinned: col.pinned,
          cellClass: "cell-left-align",
          wrapText: true,
          autoHeight: true,
        };
      }

      if (col.type === "hyperlink") {
        return {
          headerName: col.headerName || col.display_name,
          field: col.field,
          hide: col.hide ?? false,
          floatingFilter: false,
          suppressMenu: false,
          filter: "agTextColumnFilter",    
          filterParams: col.filterParams,
          cellRenderer: hyperlinkRenderer,
          cellRendererParams: {
            redirect_link: col.redirect_link,
            redirect_value: col.redirect_value,
            is_workflow_link: col.is_workflow_link,
            functionTo: col.functionTo,
          },
          pinned: col.pinned,
          width: col.width,
          wrapText: true,
          autoHeight: true,
          cellClass: "cell-left-align",
          cellStyle: WRAP_CELL_STYLE,
        };
      }

      if (col.type === "customLink") {
        return {
          headerName: col.headerName || col.display_name,
          field: col.field,
          hide: col.hide ?? false,
          sortable: true,
          suppressMenu: false,
          filter: "agTextColumnFilter",    
          filterParams: col.filterParams,
          width: col.width,
          pinned: col.pinned,
          wrapText: true,
          autoHeight: true,
          cellClass: "cell-left-align",
          cellStyle: WRAP_CELL_STYLE,
          cellRenderer: (params) => (
            <span
              style={{ color: "#007bff", cursor: "pointer" }}
              onClick={() => col.functionTo && col.functionTo(params.data)}
            >
              {params.value}
            </span>
          ),
        };
      }

      if (col.type === "customBadge") {
        return {
          headerName: col.headerName || col.display_name,
          field: col.field,
          hide: col.hide ?? false,
          sortable: true,
          suppressMenu: true,
          width: col.width,
          pinned: col.pinned,
          cellStyle: { textAlign: "center" },
          cellRenderer: (params) => {
            const val = params.value?.toString().toLowerCase();
            const isYes = val === "yes" || val === "true" || val === "1";
            const badgeStyle = {
              padding: "3px 12px",
              borderRadius: "8px",
              fontWeight: "600",
              color: isYes ? "#198754" : "#dc3545",
              backgroundColor: isYes ? "rgba(25,135,84,0.15)" : "rgba(220,53,69,0.15)",
              fontSize: "13px",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "45px",
              height: "24px",
              lineHeight: "normal",
            };
            return (
              <div style={{ display: "flex", marginTop: "2%", height: "100%" }}>
                <span style={badgeStyle}>{isYes ? "YES" : "NO"}</span>
              </div>
            );
          },
        };
      }

      if (col.type === "customButton") {
        return {
          headerName: col.headerName || col.display_name,
          field: col.field,
          hide: col.hide ?? false,
          sortable: true,
          suppressMenu: true,
          width: col.width,
          pinned: col.pinned,
          wrapText: true,
          autoHeight: true,
          cellRenderer: (params) => {
            const label = col.dynamicLabel
              ? col.dynamicLabel(params.data)
              : params.value || "View";
            return (
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "flex-start", height: "100%", paddingTop: "1px" }}>
                <span
                  style={{ color: "#0d6efd", cursor: "pointer", lineHeight: "1.2", fontWeight: 500 }}
                  onClick={() => col.functionTo && col.functionTo(params.data)}
                >
                  {label}
                </span>
              </div>
            );
          },
        };
      }

      if (col.type === "priority") {
        return {
          ...col,
          cellRenderer: PriorityRenderer,
          cellRendererFramework: undefined,
          comparator: priorityComparator,
          sortable: true,
          floatingFilter: false,
          suppressMenu: true,
          cellClass: "cell-left-align",
          wrapText: true,
          autoHeight: true,
        };
      }

      return col;
    });
  }, [columnDefs, clickAdd, clickEdit, clickDelete, clickView, clickDownload, clickStatus]);

  // ── Checkbox column ───────────────────────────────────────────────────────
  const columnsWithCheckbox = useMemo(() => ([
    {
      colId: 'checkbox',
      field: 'checkbox',
      headerName: '',
      headerComponent: CheckboxHeader,
      checkboxSelection: true,
      showDisabledCheckboxes: true,
      width: 50,
      minWidth: 50,
      maxWidth: 50,
      pinned: 'left',
      lockPosition: 'left',
      lockPinned: true,
      suppressMenu: true,
      suppressMovable: true,
      sortable: false,
      filter: false,
      resizable: false,
      suppressColumnsToolPanel: true,
      suppressFiltersToolPanel: true,
      cellStyle: {
        whiteSpace: 'normal',
        wordBreak: 'normal',
        overflowWrap: 'break-word',
        lineHeight: '1.5',
        paddingTop: '10px',
        paddingBottom: '10px',
      },
    },
    ...processedColumnDefs,
  ]), [processedColumnDefs]);

  // ── Server-side datasource ────────────────────────────────────────────────
  const datasource = useMemo(() => {
    if (clientSide) return null;
    return {
      getRows: (params) => {
        const { startRow, endRow, filterModel, sortModel } = params.request;
        const pageSize = endRow - startRow;
        const page = Math.floor(startRow / pageSize) + 1;
        let sort = null;
        if (sortModel?.length > 0) sort = { name: sortModel[0].colId, sort: sortModel[0].sort };

        instance
          .post(apiEndpoint, { ...apiPayload, filter: filterModel || {}, sort, page, pageSize })
          .then((res) => {
            params.success({ rowData: res.data.data || res.data, rowCount: res.data.totalRecords });

            if (!hasAppliedInitialOrder.current) {
              hasAppliedInitialOrder.current = true;
              const filter = externalFilterRef.current;
              const colApi = columnApiRef.current;
              setTimeout(() => {
                if (colApi && filter?.columnOrder?.length) {
                  const newState = buildOrderedState(
                    colApi.getColumnState(),
                    filter.columnOrder,
                    filter.columnVisibility || {}
                  );
                  colApi.applyColumnState({ state: newState, applyOrder: true });
                }
                if (onGridStabilizedRef.current) onGridStabilizedRef.current();
              }, 50);
            }
          })
          .catch(() => params.fail());
      },
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSide, apiEndpoint, apiPayload]);

  // ── onGridReady (single, unified) ─────────────────────────────────────────
  const onGridReady = useCallback((params) => {
    const colApi = params.columnApi ?? params.api;
    setColumnApi(colApi);
    columnApiRef.current = colApi;
    initialFilterRef.current = externalFilter;

    if (!clientSide) {
      params.api.setServerSideDatasource(datasource);
    }

    applyExternalFilter(params.api, colApi, externalFilter, clientSide);
  }, [clientSide, datasource, externalFilter]);

  // ── Loading overlay (client-side only) ────────────────────────────────────
  useEffect(() => {
    if (!clientSide || !gridRef.current?.api) return;
    loading
      ? gridRef.current.api.showLoadingOverlay()
      : gridRef.current.api.hideOverlay();
  }, [clientSide, loading]);

  // ── Watch externalFilter changes ──────────────────────────────────────────
  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api || !externalFilter) return;
    if (initialFilterRef.current === externalFilter) return;

    const colApi = columnApiRef.current ?? api;
    const prevFilter = initialFilterRef.current;
    initialFilterRef.current = externalFilter;

    applyExternalFilter(api, colApi, externalFilter, clientSide);

    // Server-side: only refresh if filter/sort actually changed
    if (!clientSide) {
      const filterChanged = JSON.stringify(prevFilter?.filterModel) !== JSON.stringify(externalFilter.filterModel);
      const sortChanged = JSON.stringify(prevFilter?.sortModel) !== JSON.stringify(externalFilter.sortModel);
      if (filterChanged || sortChanged) {
        hasAppliedInitialOrder.current = false;
      }
    }
  }, [externalFilter, clientSide]);

  // ── Filter / sort change handlers ─────────────────────────────────────────
  const handleFilterChanged = useCallback(() => {
    if (!gridRef.current?.api || !onFilterChanged) return;
    onFilterChanged(gridRef.current.api.getFilterModel());
  }, [onFilterChanged]);

  const handleSortChanged = useCallback(() => {
    if (!columnApiRef.current || !onSortChanged) return;
    const sortModel = columnApiRef.current
      .getColumnState()
      .filter((col) => col.sort != null)
      .map((col) => ({ colId: col.colId, sort: col.sort }));
    onSortChanged(sortModel);
  }, [onSortChanged]);

  // ── Filter criteria builder ───────────────────────────────────────────────
  function buildCurrentFilterCriteria(api, colApi) {
    if (!api || !colApi) return null;
    const filterModel = api.getFilterModel();
    const colState = colApi.getColumnState();
    const sortModel = colState
      .filter((c) => c.sort)
      .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
      .map((c) => ({ colId: c.colId, sort: c.sort }));
    const columnVisibility = Object.fromEntries(colState.map((c) => [c.colId, !c.hide]));
    const columnOrder = colState.map((c) => ({ colId: c.colId, width: c.width, pinned: c.pinned || null }));
    return { filterModel, sortModel, columnVisibility, columnOrder };
  }

  // ── Save filter ───────────────────────────────────────────────────────────
  const handleSaveFilter = useCallback((meta) => {
    const filterCriteria = buildCurrentFilterCriteria(gridRef.current?.api, columnApiRef.current);
    if (!filterCriteria || !onSaveFilter) return;
    onSaveFilter({
      filter_name: meta.filterName || "filename",
      filter_description: meta.filterDescription || "description",
      is_default: meta.isDefault,
      is_shared: meta.isShared,
      filter_type: filterType,
      filter_criteria: filterCriteria,
    });
  }, [onSaveFilter, filterType]);

    // ── Export / Clear ────────────────────────────────────────────────────────
  const [exporting, setExporting] = useState(false);
  const [exportEnabled, setExportEnabled] = useState(false);

  const handleSelectionChanged = useCallback((e) => {
    setExportEnabled(e.api.getSelectedRows().length > 0);
    onSelectionChanged?.(e);
  }, [onSelectionChanged]);

  const handleExport = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    const columnKeys = getExportColumns(columnApiRef.current).map((c) => c.field);
    if (api.getSelectedRows().length === 0) return;
    api.exportDataAsCsv({
      fileName: `${exportFileName}.csv`,
      columnKeys,
      onlySelected: true,
    });
  }, [exportFileName]);

  const handleExportAll = useCallback(async () => {
    const api = gridRef.current?.api;
    const colApi = columnApiRef.current;
    if (!api || !colApi) return;
    const cols = getExportColumns(colApi);

    if (clientSide) {
      downloadCsv(rowsToCsv(rowData, cols), `${exportFileName}_all.csv`);
      return;
    }

    const sortCol = colApi.getColumnState()
      .filter((c) => c.sort)
      .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))[0];
    const sort = sortCol ? { name: sortCol.colId, sort: sortCol.sort } : null;

    setExporting(true);
    try {
      const res = await instance.post(apiEndpoint, {
        ...apiPayload,
        filter: api.getFilterModel() || {},
        sort,
        page: 1,
        pageSize: exportAllPageSize,
      });
      const rows = res.data.data || res.data || [];
      downloadCsv(rowsToCsv(rows, cols), `${exportFileName}_all.csv`);
    } catch (e) {
      console.error('Export all failed', e);
    } finally {
      setExporting(false);
    }
  }, [clientSide, rowData, apiEndpoint, apiPayload, exportFileName, exportAllPageSize]);

  const handleClearFilter = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    api.setFilterModel(null);
    columnApiRef.current?.applyColumnState?.({ defaultState: { sort: null } });
    api.deselectAll();
    api.onFilterChanged(); // SSRM refetches automatically
  }, []);

  // ── Render ────────────────────────────────────────────────────────────────
  
  return (
    <> 
          {showExport && (
            <Button
              style={{ minWidth: 115 }}
              className="default-btn-color mb-1 me-1 py-1 d-inline-flex align-items-center justify-content-center gap-2"
              onClick={handleExport}
              disabled={!exportEnabled}
            >
              <FaFileExport /> {EXPORT}
            </Button>
          )}
          {showExportAll && (
            <Button
              style={{ minWidth: 115 }}
              className="default-btn-color mb-1 me-1 py-1 d-inline-flex align-items-center justify-content-center gap-2"
              onClick={handleExportAll}
              disabled={exporting}
            >
            <FaFileArrowDown /> {exporting ? "Exporting..." : "Export All"}
            </Button>
          )}
          {showClearFilter && (
            <Button
              style={{ minWidth: 115 }}
              className="default-btn-color mb-1 py-1 d-inline-flex align-items-center justify-content-center gap-2"
              onClick={handleClearFilter}
            >
              <FaFilterCircleXmark /> {"Clear Filters"}
            </Button>
          )}

    <div
      style={{ width: "100%", height: tableHeight }}
      id="server-side-grid-wrapper"
      className={`ag-theme-${theme}`}
    >
      <AgGridReact
        ref={gridRef}
        // Mode-specific
        rowModelType={clientSide ? "clientSide" : "serverSide"}
        {...(!clientSide && {
          serverSideStoreType: "partial",
          cacheBlockSize,
          maxBlocksInCache: 10,
        })}
        rowData={clientSide ? rowData : undefined}
        // Pagination
        pagination={showPagination}
        paginationPageSize={paginationPageSize}
        suppressPaginationPanel={false}
        // Columns
        columnDefs={columnsWithCheckbox}
        defaultColDef={defaultColumnDef}
        // Events
        onGridReady={onGridReady}
        onRowClicked={onRowClicked}
        onFilterChanged={handleFilterChanged}
        onSortChanged={handleSortChanged}
        onSelectionChanged={handleSelectionChanged}
        onColumnVisible={onColumnStateChanged}
        onColumnMoved={onColumnStateChanged}
        onColumnResized={onColumnStateChanged}
        // Options
        rowHeight={rowHeight}
        enableBrowserTooltips={true}
        tooltipShowDelay={300}
        suppressRowClickSelection={true}
        rowSelection="multiple"
        sortingOrder={["desc", "asc"]}
        // Sidebar
        sideBar={{
          toolPanels: [
            {
              id: 'filters',
              labelDefault: 'Filters',
              labelKey: 'filters',
              iconKey: 'filter',
              toolPanel: 'agFiltersToolPanel',
              toolPanelParams: {
                suppressExpandAll: false,
                suppressFilterSearch: false,
                suppressSyncLayoutWithGrid: false,
              },
            },
            {
              id: 'columns',
              labelDefault: 'Columns',
              labelKey: 'columns',
              iconKey: 'columns',
              toolPanel: 'agColumnsToolPanel',
              toolPanelParams: {
                suppressRowGroups: true,
                suppressValues: true,
                suppressPivots: true,
                suppressPivotMode: true,
                suppressColumnFilter: false,
                suppressColumnSelectAll: false,
                suppressColumnExpandAll: false,
              },
            },
          ],
          position: 'right',
        }}
      />
    </div>
    </>
  );
});

ServerSideGrid.displayName = 'ServerSideGrid';

export default ServerSideGrid;